import { useCallback, useEffect, useRef, useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export interface MentionUser {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  users: MentionUser[];
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export function MentionTextarea({
  value,
  onChange,
  users,
  placeholder,
  className,
  autoFocus,
  onKeyDown,
}: MentionTextareaProps) {
  const formatName = useEmployeeNameFormatter();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionState, setMentionState] = useState<{
    active: boolean;
    query: string;
    startPos: number;
  }>({ active: false, query: '', startPos: 0 });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredUsers = mentionState.active
    ? users.filter((u) => {
        const q = mentionState.query.toLowerCase();
        if (!q) return true;
        // Match either name order so search follows the Name Display setting.
        const full = `${u.firstName} ${u.lastName}`.toLowerCase();
        const display = formatName({ firstName: u.firstName, lastName: u.lastName }).toLowerCase();
        return (
          full.includes(q) ||
          display.includes(q) ||
          u.firstName.toLowerCase().startsWith(q) ||
          u.lastName.toLowerCase().startsWith(q)
        );
      }).slice(0, 8)
    : [];

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [mentionState.query]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      onChange(newValue);

      const cursorPos = e.target.selectionStart;
      const textBeforeCursor = newValue.substring(0, cursorPos);

      // Find the last @ not preceded by a word char
      const atMatch = textBeforeCursor.match(/(?:^|[\s(])@(\w*)$/);

      if (atMatch) {
        const query = atMatch[1];
        const startPos = cursorPos - query.length - 1;
        setMentionState({ active: true, query, startPos });
      } else {
        setMentionState((prev) => (prev.active ? { ...prev, active: false, query: '' } : prev));
      }
    },
    [onChange],
  );

  const insertMention = useCallback(
    (user: MentionUser) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const mention = `@${user.firstName} ${user.lastName}`;
      const before = value.substring(0, mentionState.startPos);
      const after = value.substring(textarea.selectionStart);
      const newValue = `${before}${mention} ${after}`;

      onChange(newValue);
      setMentionState({ active: false, query: '', startPos: 0 });

      requestAnimationFrame(() => {
        textarea.focus();
        const pos = before.length + mention.length + 1;
        textarea.setSelectionRange(pos, pos);
      });
    },
    [value, mentionState.startPos, onChange],
  );

  const handleKeyDownInternal = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (mentionState.active && filteredUsers.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((i) => (i + 1) % filteredUsers.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((i) => (i - 1 + filteredUsers.length) % filteredUsers.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertMention(filteredUsers[selectedIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMentionState((prev) => ({ ...prev, active: false }));
          return;
        }
      }
      onKeyDown?.(e);
    },
    [mentionState.active, filteredUsers, selectedIndex, insertMention, onKeyDown],
  );

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDownInternal}
        placeholder={placeholder}
        className={className}
        autoFocus={autoFocus}
      />

      {/* Mention popup — positioned above the textarea */}
      {mentionState.active && filteredUsers.length > 0 && (
        <div
          className="absolute z-50 w-[220px] rounded-lg border border-border bg-popover shadow-lg overflow-hidden animate-fade-in"
          style={{ bottom: '100%', left: 0, marginBottom: 4 }}
        >
          <div className="py-1 max-h-[200px] overflow-y-auto">
            {filteredUsers.map((user, i) => (
              <button
                key={user.id}
                type="button"
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors cursor-pointer',
                  i === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
                )}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent textarea blur
                  insertMention(user);
                }}
                onMouseEnter={() => setSelectedIndex(i)}
              >
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="h-6 w-6 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 text-[10px] font-semibold text-muted-foreground">
                    {user.firstName[0]}
                    {user.lastName[0]}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {formatName({ firstName: user.firstName, lastName: user.lastName })}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
